import secureApi from "../../utils/secureApi";

type AssetType = "video" | "thumbnail";

interface PresignedUpload {
  presignedUrl: string;
  key: string;
}

interface UploadSflTvVideoAssetOptions {
  file: File;
  tournamentId: string;
  seasonId: string;
  sequence: string;
  asset: AssetType;
}

const imageExtensions: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const unwrap = <T,>(response: any): T =>
  (response?.data?.data ?? response?.data ?? response) as T;

export const createVideoAssetSequence = () => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const values = new Uint8Array(8);

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(values);
  } else {
    for (let index = 0; index < values.length; index += 1) {
      values[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(values, (value) => alphabet[value % alphabet.length]).join("");
};

export const getVideoAssetSequence = (value?: string) => {
  const fileName = value?.split(/[?#]/)[0].split("/").pop();
  const match = fileName?.match(/^([a-z0-9]{8})(?:_thumbnail)?\.(?:mp4|jpe?g|png|webp)$/i);
  return match?.[1]?.toLowerCase() || "";
};

export const toStoredVideoAssetKey = (value?: string) => {
  if (!value) return value;
  const pathStart = value.indexOf("shared/");
  return pathStart >= 0 ? value.slice(pathStart).split(/[?#]/)[0] : value;
};

const fileNameFor = (file: File, sequence: string, asset: AssetType) => {
  if (asset === "video") {
    if (file.type !== "video/mp4") {
      throw new Error("Please select an MP4 video file.");
    }
    return `${sequence}.mp4`;
  }

  const extension = imageExtensions[file.type];
  if (!extension) {
    throw new Error("Please select a JPG, PNG, or WebP thumbnail.");
  }
  return `${sequence}_thumbnail.${extension}`;
};

export async function uploadSflTvVideoAsset({
  file,
  tournamentId,
  seasonId,
  sequence,
  asset,
}: UploadSflTvVideoAssetOptions): Promise<string> {
  const fileName = fileNameFor(file, sequence, asset);
  const context = `videos/${tournamentId}/${seasonId}`;
  const upload = unwrap<PresignedUpload>(
    await secureApi.post("/api/v1/general/presigned-url", {
      fileName,
      contentType: file.type,
      context,
    }),
  );

  if (!upload.presignedUrl || !upload.key) {
    throw new Error("The upload service did not return a presigned URL.");
  }

  const response = await fetch(upload.presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error("File upload failed.");
  }

  return upload.key;
}
